// src/pages/InventoryPage.js
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Package, Users, ShoppingBag, Truck, 
  BarChart2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const InventoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isDemoMode } = useAuth();
  const path = location.pathname.split('/');
  
  // Determine active tab based on URL
  const getActiveTab = () => {
    if (path.includes('items')) return 'items';
    if (path.includes('suppliers')) return 'suppliers';
    if (path.includes('buying')) return 'buying';
    if (path.includes('receiving')) return 'receiving';
    if (path.includes('reports')) return 'reports';
    return 'items'; // Default tab
  };
  
  const activeTab = getActiveTab();
  
  // Handle tab change
  const handleTabChange = (tab) => {
    switch (tab) {
      case 'items':
        navigate('/inventory/items');
        break;
      case 'suppliers':
        navigate('/inventory/suppliers');
        break;
      case 'buying':
        navigate('/inventory/buying');
        break;
      case 'receiving':
        navigate('/inventory/receiving');
        break;
      case 'reports':
        navigate('/inventory/reports');
        break;
      default:
        navigate('/inventory/items');
    }
  };
  
  return (
    <div className="space-y-6 overflow-hidden">
      {/* Header */}
      
        
              <p className="text-purple-800 text-sm mb-2">
                Manage your inventory, suppliers, purchases, and stock levels.
              </p>
              <p className="text-xs text-gray-500 mb-2">
                {isDemoMode
                  ? 'Guest mode: inventory tabs are using demo/mock data.'
                  : 'Signed-in mode: inventory tabs load your real business data.'}
              </p>
            
        
        {/* Tabs */}
        <div className="px-6 bg-white">
          <div className="flex overflow-x-auto">
            <button 
              onClick={() => handleTabChange('items')}
              className={`py-4 px-6 flex items-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'items' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package size={18} className="mr-2" />
              Items
            </button>
            
            <button 
              onClick={() => handleTabChange('suppliers')}
              className={`py-4 px-6 flex items-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'suppliers' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={18} className="mr-2" />
              Suppliers
            </button>
            
            <button 
              onClick={() => handleTabChange('buying')}
              className={`py-4 px-6 flex items-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'buying' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ShoppingBag size={18} className="mr-2" />
              Purchase Orders
            </button>
            
            <button 
              onClick={() => handleTabChange('receiving')}
              className={`py-4 px-6 flex items-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'receiving' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Truck size={18} className="mr-2" />
              Receiving
            </button>
            
            <button 
              onClick={() => handleTabChange('reports')}
              className={`py-4 px-6 flex items-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'reports' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart2 size={18} className="mr-2" />
              Reports
            </button>
          </div>
        
      </div>

      {/* Nested Routes */}
      <Outlet context={{ currentUser, isDemoMode }} />
    </div>
  );
};

export default InventoryPage;
